import { z } from "zod";
import { fromISODate, toISODate } from "@/lib/date";

/**
 * Discovery state lives in the URL.
 *
 * There used to be three copies of it — a reducer, a "staged" filter object and
 * a "local" search object — each written by a different handler. Any two of
 * them could disagree, and on desktop they routinely did: the panel wrote the
 * new value and the Apply handler immediately wrote the previous one back from
 * a stale closure, so a filter appeared to revert the instant it was tapped.
 *
 * One copy, in the URL, removes that whole class of bug and makes a search
 * shareable, restorable and correct under the back button.
 */

/*
  An absent list stays absent rather than becoming `[]`. The router serialises
  whatever the schema produces back into the address bar, so returning an empty
  array here put a literal `?am=[]` in the URL of every fresh visit.
*/
const csv = z
  .string()
  .optional()
  .transform((value) => {
    const items = value
      ? value
          .split(",")
          .map((item) => item.trim())
          .filter(Boolean)
      : [];

    return items.length > 0 ? items : undefined;
  });

const isoDate = z
  .string()
  .optional()
  .transform((value) => (fromISODate(value ?? null) ? value : undefined));

export const SORT_OPTIONS = ["recommended", "price_asc", "price_desc", "rating", "newest"] as const;

export type SortOption = (typeof SORT_OPTIONS)[number];

export const discoverySearchSchema = z.object({
  /** Free-text destination. */
  q: z.string().optional().catch(undefined),
  /** Check-in / check-out, `YYYY-MM-DD`. */
  in: isoDate.catch(undefined),
  out: isoDate.catch(undefined),
  guests: z.coerce.number().int().min(1).max(30).optional().catch(undefined),
  min: z.coerce.number().min(0).optional().catch(undefined),
  max: z.coerce.number().min(0).optional().catch(undefined),
  /** Property type, one of the server's published vocabulary values. */
  cat: z.string().optional().catch(undefined),
  /** Room type: entire place, private room or shared room. */
  room: z.string().optional().catch(undefined),
  /** Amenities, comma separated. */
  am: csv.catch(undefined),
  /** Minimum bedrooms / bathrooms. */
  bd: z.coerce.number().int().min(0).max(8).optional().catch(undefined),
  ba: z.coerce.number().int().min(0).max(8).optional().catch(undefined),
  /** Instant book only. */
  ib: z.coerce.boolean().optional().catch(undefined),
  sort: z.enum(SORT_OPTIONS).optional().catch(undefined),
  /** Map viewport, only present while the guest is browsing the map. */
  ne_lat: z.coerce.number().optional().catch(undefined),
  ne_lng: z.coerce.number().optional().catch(undefined),
  sw_lat: z.coerce.number().optional().catch(undefined),
  sw_lng: z.coerce.number().optional().catch(undefined),
});

export type DiscoverySearch = z.infer<typeof discoverySearchSchema>;

/** The empty search — every key absent so the URL stays clean. */
export const emptyDiscoverySearch: DiscoverySearch = {};

/**
 * The parameters the discovery API actually understands.
 *
 * Built in one place so the query key, the request and the "what is filtering
 * my results" chip row can never describe different searches.
 */
export interface DiscoveryApiParams {
  search?: string;
  check_in?: string;
  check_out?: string;
  guests?: number;
  minPrice?: number;
  maxPrice?: number;
  property_type?: string;
  room_type?: string;
  amenities?: string;
  bedrooms?: number;
  bathrooms?: number;
  instant_book?: boolean;
  sort?: SortOption;
  ne_lat?: number;
  ne_lng?: number;
  sw_lat?: number;
  sw_lng?: number;
}

/**
 * Translate URL state into request parameters.
 *
 * Only meaningful values are emitted. A `bedrooms=0` or an empty `search=`
 * would be a filter the server has to evaluate and the cache has to key on,
 * for a constraint that excludes nothing.
 */
export function toApiParams(search: DiscoverySearch): DiscoveryApiParams {
  const params: DiscoveryApiParams = {};

  if (search.q?.trim()) params.search = search.q.trim();

  // Dates only constrain availability as a complete range; a lone check-in
  // date cannot decide whether anything is free.
  if (search.in && search.out) {
    params.check_in = search.in;
    params.check_out = search.out;
  }

  if (search.guests && search.guests > 1) params.guests = search.guests;
  if (typeof search.min === "number" && search.min > 0) params.minPrice = search.min;
  if (typeof search.max === "number" && search.max > 0) params.maxPrice = search.max;
  if (search.cat) params.property_type = search.cat;
  if (search.room) params.room_type = search.room;
  if (search.am?.length) params.amenities = search.am.join(",");
  if (search.bd && search.bd > 0) params.bedrooms = search.bd;
  if (search.ba && search.ba > 0) params.bathrooms = search.ba;
  if (search.ib) params.instant_book = true;
  if (search.sort && search.sort !== "recommended") params.sort = search.sort;

  const hasBounds =
    typeof search.ne_lat === "number" &&
    typeof search.ne_lng === "number" &&
    typeof search.sw_lat === "number" &&
    typeof search.sw_lng === "number";

  if (hasBounds) {
    params.ne_lat = search.ne_lat;
    params.ne_lng = search.ne_lng;
    params.sw_lat = search.sw_lat;
    params.sw_lng = search.sw_lng;
  }

  return params;
}

/** The dates as Date objects, for the pickers. */
export function searchDates(search: DiscoverySearch): { from: Date | null; to: Date | null } {
  return { from: fromISODate(search.in ?? null), to: fromISODate(search.out ?? null) };
}

/** Write a date range back into URL form. */
export function datesToSearch(
  from: Date | null,
  to: Date | null,
): Pick<DiscoverySearch, "in" | "out"> {
  return {
    in: from ? toISODate(from) : undefined,
    out: to ? toISODate(to) : undefined,
  };
}

/**
 * How many *filters* are active, excluding the search capsule's own fields.
 *
 * Drives the badge on the Filters button, which should not light up merely
 * because the guest typed a destination.
 */
export function activeFilterCount(search: DiscoverySearch): number {
  return (
    (typeof search.min === "number" && search.min > 0 ? 1 : 0) +
    (typeof search.max === "number" && search.max > 0 ? 1 : 0) +
    (search.cat ? 1 : 0) +
    (search.am?.length ?? 0) +
    (search.bd ? 1 : 0) +
    (search.ba ? 1 : 0) +
    (search.ib ? 1 : 0)
  );
}

/** True when the guest has narrowed discovery in any way at all. */
export function hasAnySearch(search: DiscoverySearch): boolean {
  return (
    activeFilterCount(search) > 0 ||
    Boolean(search.q?.trim()) ||
    Boolean(search.in) ||
    Boolean(search.out) ||
    (search.guests ?? 1) > 1
  );
}
